import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabaseClient';
import { FiLoader } from 'react-icons/fi';
import { Toaster } from 'react-hot-toast';

// ✅ Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// ===================================================================================
// ✅ COMPOSANT ProtectedRoute (quasi inchangé)
// ===================================================================================
const ProtectedRoute = ({ session, userProfile, isLoading, refreshUserProfile }) => {
  const location = useLocation();

  const outletContext = React.useMemo(
    () => ({ userProfile, session, refreshUserProfile }),
    [userProfile, session, refreshUserProfile]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <FiLoader className="animate-spin mx-auto h-8 w-8 text-blue-600 mb-4" />
          <p>Chargement de votre session...</p>
        </div>
      </div>
    );
  }

  // 🔒 Si non connecté → login (et on mémorise la page demandée)
  if (!session || !userProfile) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet context={outletContext} />;
};

// ===================================================================================
// ✅ APP
// ===================================================================================
export default function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [authData, setAuthData] = useState({ session: null, profile: null });

  const location = useLocation();
  const navigate = useNavigate();

  const refreshAuthData = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      let profile = null;
      if (session?.user) {
        const { data: userProfile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (error) throw error;
        profile = userProfile;
      }

      setAuthData({ session, profile });
    } catch (error) {
      console.error('[App.jsx] Erreur refreshAuthData:', error.message);
      setAuthData({ session: null, profile: null });
    } finally {
      setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuthData();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      // MFA_CHALLENGE_VERIFIED : le code de double authentification vient d'être présenté
      // (jeton aal2) → le profil, refusé par la base au mot de passe seul, devient lisible.
      if (['SIGNED_IN', 'SIGNED_OUT', 'MFA_CHALLENGE_VERIFIED'].includes(_event)) {
        refreshAuthData();
      }

      if (_event === 'SIGNED_OUT') {
        setAuthData({ session: null, profile: null });
        navigate('/login', { replace: true });
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [refreshAuthData, navigate]);

  // ✅ Si l’utilisateur est connecté et arrive sur /login → dashboard
  useEffect(() => {
    if (isInitialLoading) return;

    const { profile } = authData;
    if (profile && location.pathname === '/login') {
      navigate('/dashboard', { replace: true });
    }
  }, [authData, isInitialLoading, location.pathname, navigate]);

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            padding: '16px',
            color: '#1F2937',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
          success: { iconTheme: { primary: '#10B981', secondary: 'white' } },
          error: { iconTheme: { primary: '#EF4444', secondary: 'white' } },
        }}
      />

      <Routes>
        {/* Public */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage isLoadingSession={isInitialLoading} />} />

        {/* Protégé */}
        <Route
          element={
            <ProtectedRoute
              session={authData.session}
              userProfile={authData.profile}
              isLoading={isInitialLoading}
              refreshUserProfile={refreshAuthData}
            />
          }
        >
          <Route path="/dashboard/*" element={<DashboardPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}
